/**
 * Number formatting for readings (telemetry, title cards), localized for en/de. Pure functions, unit-tested.
 */
import type { Lang } from '../i18n';

const locale = (l: Lang): string => (l === 'de' ? 'de-DE' : 'en-GB');

const formatters = new Map<string, Intl.NumberFormat>();
function nf(l: Lang, digits: number): Intl.NumberFormat {
  const key = `${l}:${digits}`;
  let f = formatters.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale(l), { minimumFractionDigits: digits, maximumFractionDigits: digits });
    formatters.set(key, f);
  }
  return f;
}

/**
 * Loads the language's number and date data up front (S4-03): the first Intl formatter of a page costs ~60 ms at 4×
 * CPU in Chromium, which otherwise lands in the HUD mount and the first frame. One formatter per task (part 6: all at
 * once were one task above Lighthouse's 50 ms).
 */
export async function warmFormatters(l: Lang): Promise<void> {
  const nextTask = (): Promise<void> => new Promise((resolve) => setTimeout(resolve, 0));
  for (const digits of [0, 1, 2]) {
    nf(l, digits).format(1);
    await nextTask();
  }
  new Intl.DateTimeFormat(locale(l), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(0);
}

/** `25.2` / `25,2` (km/s). */
export function formatSpeed(kms: number, l: Lang): string {
  return nf(l, 1).format(kms);
}

/** `5.33` / `5,33` (AU). */
export function formatAu(au: number, l: Lang): string {
  return nf(l, au < 100 ? 2 : 1).format(au);
}

/** Distance in km: grouped integer below 10 million km, otherwise millions with one decimal. */
export function formatKm(km: number, l: Lang): { value: string; millions: boolean } {
  if (km < 1e7) return { value: nf(l, 0).format(Math.round(km)), millions: false };
  return { value: nf(l, 1).format(km / 1e6), millions: true };
}

/** Light time: `12 s`, `51.8 min`, `4 h 06 min`. */
export function formatLightTime(seconds: number, l: Lang): string {
  if (seconds < 60) return `${Math.round(seconds)} s`;
  const minutes = seconds / 60;
  if (minutes < 100) return `${nf(l, 1).format(minutes)} min`;
  const total = Math.round(minutes);
  return `${Math.floor(total / 60)} h ${String(total % 60).padStart(2, '0')} min`;
}

/** Grouped integer: `4,410` / `4.410`. */
export function formatInt(n: number, l: Lang): string {
  return nf(l, 0).format(n);
}
